/**
 * Copy `text` to the clipboard, returning whether it succeeded.
 *
 * The async Clipboard API is only available in a secure context (https). Local
 * dev runs over http (e.g. dawn.lvh.me), where `navigator.clipboard` is
 * undefined — so fall back to a hidden-textarea + `execCommand('copy')`.
 * Deprecated, but it is the only thing that works outside a secure context.
 *
 * `mount` is the element the fallback textarea is appended to (default
 * `document.body`). This matters inside a focus-trapped modal (Radix Dialog):
 * a textarea appended to `document.body` lives OUTSIDE the dialog's focus
 * scope, so focusing it to build the selection makes the scope synchronously
 * yank focus back into the dialog — leaving an empty selection and copying
 * nothing. Pass an element inside the dialog so the selection survives.
 */
export async function copyText(text: string, mount?: HTMLElement | null): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path below.
  }

  const previouslyFocused = document.activeElement as HTMLElement | null;
  try {
    const container = mount ?? document.body;
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    container.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand('copy');
    container.removeChild(textarea);
    // Restore focus so removing the textarea doesn't drop the dialog's focus.
    previouslyFocused?.focus?.();
    return ok;
  } catch {
    return false;
  }
}
